import React from 'react';
import {
  CONTACTING_IMAGE_URL,
  COOKING_IMAGE_URL,
  ORDER_FLOW_TYPE,
  ORDER_PHASE_CONTACTING,
  ORDER_PHASE_DELIVERING,
  ORDER_PHASE_PREPARING,
  ORDER_PHASE_TAKEAWAY,
  ORDER_PHASE_WAITER,
  ORDER_TYPE_DELIVERY,
  PICKUP_STATUS,
  UNIT,
  WAITER_IMAGE_URL,
  PROMOTION_DELIVERY_FREE,
  ORDER_PHASE,
} from '../../../constants';
import OrderSteps from '../../OrderSteps';
import { getStatus } from '../../../utils';
import MapClient from '../../MapClient';
import _ from 'lodash';
import { useAppSelector } from '../../../redux/hook';
import OrderStatusEmergencyDelivery from './OrderStatusEmergencyDelivery';
import OrderStatusEstimationTime from './OrderStatusEstimationTime';

type OrderStatusStatusProps = {
  order: any;
  chargeId: string;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

const calculateEmergencyClickAndCollect = (
  order: any,
  delivery: any,
  orderStatus: string
) => {
  const {
    orderFlowType,
    pickupStatus,
    orderType,
    selfPickupEligible,
    totalProductPriceOrder = 0,
    userServicePrice = 0,
    managementFee = 0,
    promotion,
  } = order;

  const orderNotCurrentlySupported =
    pickupStatus === PICKUP_STATUS.ORDER_NOT_CURRENTLY_SUPPORTED;
  const deliveryFreeReduction =
    promotion?.type === PROMOTION_DELIVERY_FREE ? 0 : userServicePrice;

  const clickAndCollectReduction =
    delivery?.emergency?.clickAndCollectConversionReduction ?? 0;

  const reductionBase = deliveryFreeReduction + managementFee;

  const reductionToClickAndCollect = clickAndCollectReduction
    ? (
      Math.round(clickAndCollectReduction * totalProductPriceOrder) / 100 +
      reductionBase
    ).toFixed(2)
    : reductionBase.toFixed(2);

  const isContactingEmergency =
    orderStatus === ORDER_PHASE.CONTACTING &&
    orderFlowType === ORDER_FLOW_TYPE.REVERSE &&
    orderNotCurrentlySupported;

  const isPreparingEmergency =
    orderStatus === ORDER_PHASE.PREPARING &&
    pickupStatus < PICKUP_STATUS.DRIVER_ON_THE_WAY_TO_RESTAURANT;

  const isDeliveryEmergency =
    (isContactingEmergency || isPreparingEmergency) &&
    orderType === ORDER_TYPE_DELIVERY &&
    selfPickupEligible &&
    delivery?.emergency?.enabled;

  return { reductionToClickAndCollect: reductionToClickAndCollect.replace('.', ','), isDeliveryEmergency };
};

export const getActiveStep = (
  status: string,
  orderFlowReverse: boolean,
  orderType: string,
  pickupStatus: number
): number => {
  switch (status) {
    case ORDER_PHASE_CONTACTING:
      return orderFlowReverse &&
        pickupStatus === PICKUP_STATUS.DRIVER_ON_THE_WAY_TO_RESTAURANT
        ? 1
        : 0;
    case ORDER_PHASE_PREPARING:
      return orderFlowReverse && orderType === ORDER_TYPE_DELIVERY ? 2 : 1;
    case ORDER_PHASE_DELIVERING:
      return orderFlowReverse && orderType === ORDER_TYPE_DELIVERY ? 3 : 2;
    case ORDER_PHASE_TAKEAWAY:
    case ORDER_PHASE_WAITER:
      return 2;
    default:
      return 0;
  }
};

export default function OrderStatusStatus({
  order,
  chargeId,
  isLoading,
  setIsLoading,
}: OrderStatusStatusProps) {
  if (_.isEmpty(order)) return null;

  const { status, orderFlowType, pickupStatus, orderType } = order;
  const { delivery } = useAppSelector((state) => state.configurationReducer);

  const { reductionToClickAndCollect, isDeliveryEmergency } =
    calculateEmergencyClickAndCollect(order, delivery, status);

  const orderFlowReverse = orderFlowType === ORDER_FLOW_TYPE.REVERSE;
  const activeStep = getActiveStep(status, orderFlowReverse, orderType, pickupStatus);

  const StatusLabel = <p className="text-left mb-0 label" style={{ fontSize: 14 }}>{getStatus(order)}</p>;
  const EmergencyBlock = isDeliveryEmergency && (
    <>
      <hr className="my-2 hr" />
      <OrderStatusEmergencyDelivery
        isDeliveryEmergency={isDeliveryEmergency}
        reductionToClickAndCollect={reductionToClickAndCollect}
        order={order}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </>
  );

  const renderContacting = () => (
    <>
      {StatusLabel}
      {EmergencyBlock}
      {orderFlowType === ORDER_FLOW_TYPE.REVERSE &&
        orderType === ORDER_TYPE_DELIVERY &&
        pickupStatus === PICKUP_STATUS.ORDER_NOT_CURRENTLY_SUPPORTED ? (
        <div className="my-2">
          <MapClient chargeId={chargeId} />
        </div>
      ) : (
        <img
          style={{ height: 160, width: 240 }}
          className="img-fluid rounded mx-auto mb-4"
          alt="contacting gif"
          src={CONTACTING_IMAGE_URL}
        />
      )}
      <OrderStatusEstimationTime order={order} />
    </>
  );

  const renderPreparing = () => (
    <>
      {StatusLabel}
      {EmergencyBlock}
      <img
        style={{ height: 150, width: 300 }}
        className="img-fluid rounded mx-auto d-block my-4"
        alt="cooking gif"
        src={COOKING_IMAGE_URL}
      />
      <OrderStatusEstimationTime order={order} />
    </>
  );

  const renderDelivering = () => (
    <>
      {StatusLabel}
      {order.validationCode && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: -5,
            paddingTop: 5,
            borderTop: '1px solid #ccc',
            marginTop: 10,
            textAlign: 'left',
          }}
        >
          <div>
            <p style={{ fontWeight: 'bold', fontSize: UNIT }}>
              Donner ce code à votre livreur
            </p>
            <p style={{ color: '#999', fontSize: 14 }}>
              Uniquement au moment de la réception de la commande
            </p>
          </div>
          <p style={{ fontSize: 36, fontWeight: 'bold' }}>{order.validationCode}</p>
        </div>
      )}
      <div className="my-2">
        <MapClient chargeId={chargeId} />
      </div>
      <OrderStatusEstimationTime order={order} />
    </>
  );

  const renderTakeaway = () => (
    <>
      {StatusLabel}
      <div className="my-2">
        <MapClient chargeId={chargeId} />
      </div>
    </>
  );

  const renderWaiter = () => (
    <>
      {StatusLabel}
      <img
        style={{ height: 300, width: 300 }}
        className="img-fluid rounded mx-auto my-4"
        alt="waiter gif"
        src={WAITER_IMAGE_URL}
      />
    </>
  );

  const renderContent = () => {
    switch (status) {
      case ORDER_PHASE_CONTACTING:
        return renderContacting();
      case ORDER_PHASE_PREPARING:
        return renderPreparing();
      case ORDER_PHASE_DELIVERING:
        return renderDelivering();
      case ORDER_PHASE_TAKEAWAY:
        return renderTakeaway();
      case ORDER_PHASE_WAITER:
        return renderWaiter();
      default:
        return (
          <img
            style={{ height: 160, width: 240 }}
            className="img-fluid rounded mx-auto my-4"
            alt="contacting gif"
            src={CONTACTING_IMAGE_URL}
          />
        );
    }
  };

  return (
    <>
      <OrderSteps order={order} activeStep={activeStep} />
      {renderContent()}
    </>
  );
}
